/**
 * Wizard card for the gtm_server_url step (Slice 9 — the guide's Phase 6).
 *
 * Adds `https://br.{clientdomain}` to the shared SERVER GTM container's tagging
 * URLs so the server container accepts the new client's events. The web
 * container (GTM-T2MJBHCP) is universal and needs no per-client edit, so it's
 * not touched here. This is an additive container-settings change (no publish).
 */
import { CheckCircle2, Loader2, RefreshCw, Server } from 'lucide-react'
import { Button } from '@/components/ui/button'
import type { OnboardingTask } from '@limo/database'
import { useInvokeOnboardingStep } from '@/hooks/useOnboarding'
import { useToast } from '@/hooks/use-toast'
import { StepCard } from './StepCard'

interface GtmServerUrlCardProps {
  task: OnboardingTask
  accountId: string
  runId: string
}

interface GtmResult {
  serverUrl?: string
  taggingServerUrls?: string[]
  reused?: boolean
}

export function GtmServerUrlCard({ task, accountId, runId }: GtmServerUrlCardProps) {
  const { toast } = useToast()
  const invokeStep = useInvokeOnboardingStep(runId)

  const result = (task.result ?? {}) as GtmResult
  const params = (task.params ?? {}) as { mainDomain?: string }
  const isDone = task.status === 'done'

  const addServerUrl = () => {
    invokeStep.mutate(
      {
        functionName: 'onboard-gtm',
        accountId,
        params: { action: 'add_server_url' },
      },
      {
        onError: (error) =>
          toast({
            title: 'GTM server URL failed',
            description: error.message,
            variant: 'destructive',
          }),
      }
    )
  }

  return (
    <StepCard
      task={task}
      manualFallback={
        <p>
          Adds <code>https://br.{params.mainDomain ?? '{domain}'}</code> to the
          shared <strong>server</strong> GTM container (Admin → Container
          Settings → Add URL). The web container is universal and isn&rsquo;t
          touched. Requires the platform service account to have access to the
          server container, and <code>GTM_SERVER_ACCOUNT_ID</code> /{' '}
          <code>GTM_SERVER_CONTAINER_ID</code> edge-function secrets.
        </p>
      }
    >
      {isDone ? (
        <div className="space-y-2 text-sm">
          <div className="flex items-center gap-2">
            <CheckCircle2 className="h-4 w-4 text-green-600" />
            <span>
              {result.reused ? 'Already in' : 'Added to'} the server container:{' '}
              <code className="rounded bg-muted px-1.5 py-0.5 font-mono text-xs">
                {result.serverUrl}
              </code>
            </span>
          </div>
          {result.taggingServerUrls && result.taggingServerUrls.length > 0 && (
            <p className="text-xs text-muted-foreground">
              Server container now has {result.taggingServerUrls.length} tagging
              URL{result.taggingServerUrls.length === 1 ? '' : 's'}.
            </p>
          )}
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={addServerUrl}
            disabled={invokeStep.isPending}
          >
            {invokeStep.isPending ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <RefreshCw className="mr-2 h-4 w-4" />
            )}
            Re-run / reconcile
          </Button>
        </div>
      ) : (
        <div className="space-y-3">
          <p className="text-sm text-muted-foreground">
            Adds <code>https://br.{params.mainDomain ?? '{domain}'}</code> to the
            shared server GTM container so it accepts this client&rsquo;s events.
          </p>
          <Button type="button" size="sm" onClick={addServerUrl} disabled={invokeStep.isPending}>
            {invokeStep.isPending ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <Server className="mr-2 h-4 w-4" />
            )}
            Add to server container
          </Button>
        </div>
      )}
    </StepCard>
  )
}
